import React, { useMemo, useState } from 'react'
import Breadcrumb from '../../components/common/Breadcrumb'
import ReactDataTable from 'react-data-table-component';
import { useAuth } from '../../contexts/AuthContext';
import { avatar1, avatar2, avatar3 } from '../../assets/images';
import { IconEye } from '@tabler/icons-react';

export default function LeaveHistory() {
    const { user, hasRole } = useAuth();
    const isAdminOrHR = hasRole(['super_admin', 'hr_admin']);

    // Leave types configuration
    const leaveTypes = {
        annual: { label: 'Annual Leave', color: 'text-blue', bgColor: 'bg-blue' },
        sick: { label: 'Sick Leave', color: 'text-green', bgColor: 'bg-green' },
        compassionate: { label: 'Compassionate Leave', color: 'text-purple', bgColor: 'bg-purple' },
        study: { label: 'Study Leave', color: 'text-orange', bgColor: 'bg-orange' },
        lwp: { label: 'Leave Without Pay', color: 'text-red', bgColor: 'bg-red' }
    };

    // Status badge helper
    const getStatusBadge = (status) => {
        const statusMap = {
            pending: <span className='badge badge-warning'>Pending</span>,
            approved: <span className='badge badge-success'>Approved</span>,
            rejected: <span className='badge badge-danger'>Rejected</span>,
            cancelled: <span className='badge badge-secondary'>Cancelled</span>,
        };
        return statusMap[status] || <span className='badge badge-secondary'>{status}</span>;
    };

    // Mock leave history data (would come from backend)
    const allLeaveHistory = [
        {
            id: 1,
            employeeId: 'LA-0215',
            employeeName: 'Marshall Nichols',
            avatar: avatar1,
            department: 'Engineering',
            leaveType: 'annual',
            fromDate: '2024-12-15',
            toDate: '2024-12-17',
            days: 3,
            reason: 'Going to Family Function',
            appliedDate: '2024-12-01',
            status: 'approved',
            approver: 'Jane Manager',
            approvalDate: '2024-12-02',
            comments: 'Approved by line manager'
        },
        {
            id: 2,
            employeeId: 'LA-0216',
            employeeName: 'Susie Willis',
            avatar: avatar2,
            department: 'HR',
            leaveType: 'sick',
            fromDate: '2024-11-21',
            toDate: '2024-11-22',
            days: 2,
            reason: 'Medical checkup',
            appliedDate: '2024-11-20',
            status: 'approved',
            approver: 'HR Admin',
            approvalDate: '2024-11-20',
            comments: 'Medical certificate provided'
        },
        {
            id: 3,
            employeeId: 'LA-0215',
            employeeName: 'Marshall Nichols',
            avatar: avatar1,
            department: 'Engineering',
            leaveType: 'study',
            fromDate: '2024-10-10',
            toDate: '2024-10-12',
            days: 3,
            reason: 'Attending AWS certification course',
            appliedDate: '2024-09-25',
            status: 'approved',
            approver: 'Jane Manager',
            approvalDate: '2024-09-26',
            comments: 'Course certificate to be submitted'
        },
        {
            id: 4,
            employeeId: 'LA-0218',
            employeeName: 'Debra Stewart',
            avatar: avatar3,
            department: 'Marketing',
            leaveType: 'compassionate',
            fromDate: '2024-09-15',
            toDate: '2024-09-17',
            days: 3,
            reason: 'Family emergency',
            appliedDate: '2024-09-14',
            status: 'approved',
            approver: 'Marketing Head',
            approvalDate: '2024-09-14',
            comments: 'Emergency approved immediately'
        },
        {
            id: 5,
            employeeId: 'LA-0215',
            employeeName: 'Marshall Nichols',
            avatar: avatar1,
            department: 'Engineering',
            leaveType: 'annual',
            fromDate: '2024-08-05',
            toDate: '2024-08-09',
            days: 5,
            reason: 'Summer vacation',
            appliedDate: '2024-07-15',
            status: 'approved',
            approver: 'Jane Manager',
            approvalDate: '2024-07-16',
            comments: 'Have a great vacation!'
        },
        {
            id: 6,
            employeeId: 'LA-0216',
            employeeName: 'Susie Willis',
            avatar: avatar2,
            department: 'HR',
            leaveType: 'lwp',
            fromDate: '2024-07-20',
            toDate: '2024-07-21',
            days: 2,
            reason: 'Personal matters',
            appliedDate: '2024-07-18',
            status: 'approved',
            approver: 'HR Admin',
            approvalDate: '2024-07-18',
            comments: 'LWP approved'
        },
        {
            id: 7,
            employeeId: 'LA-0218',
            employeeName: 'Debra Stewart',
            avatar: avatar3,
            department: 'Marketing',
            leaveType: 'annual',
            fromDate: '2024-06-10',
            toDate: '2024-06-11',
            days: 2,
            reason: 'Personal appointment',
            appliedDate: '2024-06-01',
            status: 'rejected',
            approver: 'Marketing Head',
            approvalDate: '2024-06-02',
            comments: 'Team understaffed during this period'
        },
        {
            id: 8,
            employeeId: 'LA-0215',
            employeeName: 'Marshall Nichols',
            avatar: avatar1,
            department: 'Engineering',
            leaveType: 'sick',
            fromDate: '2024-05-15',
            toDate: '2024-05-16',
            days: 2,
            reason: 'Flu symptoms',
            appliedDate: '2024-05-15',
            status: 'approved',
            approver: 'Jane Manager',
            approvalDate: '2024-05-15',
            comments: 'Get well soon'
        },
    ];

    // Filter data based on role
    const leaveHistoryData = isAdminOrHR
        ? allLeaveHistory
        : allLeaveHistory.filter(leave => leave.employeeName === user.name); // In real app, filter by user.id

    // State for filters
    const [filterText, setFilterText] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [leaveTypeFilter, setLeaveTypeFilter] = useState('all');
    const [yearFilter, setYearFilter] = useState('2024');
    const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
    const [viewDetailsModal, setViewDetailsModal] = useState(false);
    const [selectedLeave, setSelectedLeave] = useState(null);

    // Admin/HR Table columns
    const adminColumns = [
        {
            name: 'Applied',
            selector: row => row.appliedDate,
            sortable: true,
            width: '110px',
        },
        {
            name: 'Employee',
            selector: row => row.employee,
            sortable: true,
            width: '200px',
        },
        {
            name: 'Department',
            selector: row => row.department,
            sortable: true,
            width: '120px',
        },
        {
            name: 'Leave Type',
            selector: row => row.leaveType,
            sortable: true,
            width: '160px',
        },
        {
            name: 'Period',
            selector: row => row.period,
            sortable: true,
            width: '200px',
        },
        {
            name: 'Days',
            selector: row => row.days,
            sortable: true,
            width: '70px',
            center: true,
        },
        {
            name: 'Status',
            selector: row => row.status,
            sortable: true,
            width: '120px',
        },
        {
            name: 'Approver',
            selector: row => row.approver,
            sortable: true,
            width: '140px',
        },
        {
            name: 'Action',
            selector: row => row.action,
            width: '80px',
            center: true,
        },
    ];

    // Employee Table columns
    const employeeColumns = [
        {
            name: 'Applied',
            selector: row => row.appliedDate,
            sortable: true,
            width: '110px',
        },
        {
            name: 'Leave Type',
            selector: row => row.leaveType,
            sortable: true,
            width: '160px',
        },
        {
            name: 'Period',
            selector: row => row.period,
            sortable: true,
            width: '200px',
        },
        {
            name: 'Days',
            selector: row => row.days,
            sortable: true,
            width: '80px',
            center: true,
        },
        {
            name: 'Status',
            selector: row => row.status,
            sortable: true,
            width: '120px',
        },
        {
            name: 'Approver',
            selector: row => row.approver,
            sortable: true,
            width: '150px',
        },
        {
            name: 'Action',
            selector: row => row.action,
            width: '100px',
            center: true,
        },
    ];

    // Format date helper
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    // View details handler
    const handleViewDetails = (leave) => {
        setSelectedLeave(leave);
        setViewDetailsModal(true);
    };

    // Format data for table
    const tableData = leaveHistoryData.map(leave => {
        const leaveTypeData = leaveTypes[leave.leaveType];

        return {
            appliedDate: formatDate(leave.appliedDate),
            employee: isAdminOrHR ? (
                <div className='flex items-center gap-10'>
                    <img src={leave.avatar} alt={leave.employeeName} className='w-[35px] h-[35px] rounded-full object-cover' />
                    <div>
                        <p className='font-medium text-sm'>{leave.employeeName}</p>
                        <p className='text-xs text-text-secondary'>{leave.employeeId}</p>
                    </div>
                </div>
            ) : null,
            department: leave.department,
            leaveType: (
                <span className={`text-sm font-medium ${leaveTypeData.color}`}>
                    {leaveTypeData.label}
                </span>
            ),
            period: (
                <div className='text-sm'>
                    <p>{formatDate(leave.fromDate)} - {formatDate(leave.toDate)}</p>
                </div>
            ),
            days: <span className='font-medium'>{leave.days}</span>,
            status: getStatusBadge(leave.status),
            approver: (
                <div className='text-sm'>
                    <p className='font-medium'>{leave.approver}</p>
                    <p className='text-xs text-text-secondary'>{formatDate(leave.approvalDate)}</p>
                </div>
            ),
            action: (
                <button
                    onClick={() => handleViewDetails(leave)}
                    className='text-blue hover:text-blue-100 transition-colors'
                    title='View Details'
                >
                    <IconEye className='w-[20px] h-[20px]' />
                </button>
            ),
            // For filtering
            _rawData: leave
        };
    });

    // Apply filters
    const filteredItems = tableData.filter(item => {
        const leave = item._rawData;

        // Text search filter
        const searchMatch = filterText === '' ||
            leave.employeeName.toLowerCase().includes(filterText.toLowerCase()) ||
            leave.employeeId.toLowerCase().includes(filterText.toLowerCase()) ||
            leave.reason.toLowerCase().includes(filterText.toLowerCase());

        // Status filter
        const statusMatch = statusFilter === 'all' || leave.status === statusFilter;

        // Leave type filter
        const leaveTypeMatch = leaveTypeFilter === 'all' || leave.leaveType === leaveTypeFilter;

        // Year filter
        const yearMatch = yearFilter === 'all' || leave.appliedDate.startsWith(yearFilter);

        return searchMatch && statusMatch && leaveTypeMatch && yearMatch;
    });

    // Sub header with filters
    const subHeaderComponentMemo = useMemo(() => {
        const handleClear = () => {
            if (filterText || statusFilter !== 'all' || leaveTypeFilter !== 'all' || yearFilter !== '2024') {
                setResetPaginationToggle(!resetPaginationToggle);
                setFilterText('');
                setStatusFilter('all');
                setLeaveTypeFilter('all');
                setYearFilter('2024');
            }
        };

        return (
            <div className='flex flex-wrap gap-10 mb-10 w-full justify-between items-center'>
                <div className='flex flex-wrap gap-10 flex-1'>
                    {/* Search */}
                    <div className='relative w-full max-w-[200px]'>
                        <input
                            type='text'
                            id='search'
                            value={filterText}
                            onChange={e => setFilterText(e.target.value)}
                            placeholder='Search...'
                            className='bg-bg-light border border-border w-full py-[6px] px-3 min-h-[40px] rounded-[4px] placeholder:text-text-secondary focus:outline-none text-sm'
                        />
                    </div>

                    {/* Status Filter */}
                    <select
                        value={statusFilter}
                        onChange={e => setStatusFilter(e.target.value)}
                        className='bg-bg-light border border-border py-[6px] px-3 min-h-[40px] rounded-[4px] text-sm min-w-[140px]'
                    >
                        <option value="all">All Status</option>
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                        <option value="cancelled">Cancelled</option>
                    </select>

                    {/* Leave Type Filter */}
                    <select
                        value={leaveTypeFilter}
                        onChange={e => setLeaveTypeFilter(e.target.value)}
                        className='bg-bg-light border border-border py-[6px] px-3 min-h-[40px] rounded-[4px] text-sm min-w-[160px]'
                    >
                        <option value="all">All Leave Types</option>
                        <option value="annual">Annual Leave</option>
                        <option value="sick">Sick Leave</option>
                        <option value="compassionate">Compassionate Leave</option>
                        <option value="study">Study Leave</option>
                        <option value="lwp">Leave Without Pay</option>
                    </select>

                    {/* Year Filter */}
                    <select
                        value={yearFilter}
                        onChange={e => setYearFilter(e.target.value)}
                        className='bg-bg-light border border-border py-[6px] px-3 min-h-[40px] rounded-[4px] text-sm min-w-[120px]'
                    >
                        <option value="all">All Years</option>
                        <option value="2024">2024</option>
                        <option value="2023">2023</option>
                        <option value="2022">2022</option>
                    </select>
                </div>

                <button onClick={handleClear} className="btn btn-danger btn-sm">
                    Clear Filters
                </button>
            </div>
        );
    }, [filterText, statusFilter, leaveTypeFilter, yearFilter, resetPaginationToggle]);

    // Calculate statistics
    const stats = useMemo(() => {
        const totalRequests = leaveHistoryData.length;
        const approved = leaveHistoryData.filter(l => l.status === 'approved').length;
        const pending = leaveHistoryData.filter(l => l.status === 'pending').length;
        const rejected = leaveHistoryData.filter(l => l.status === 'rejected').length;
        const totalDays = leaveHistoryData.filter(l => l.status === 'approved').reduce((sum, l) => sum + l.days, 0);

        return { totalRequests, approved, pending, rejected, totalDays };
    }, [leaveHistoryData]);

    return (
        <>
            <Breadcrumb
                title={isAdminOrHR ? "Leave History" : "Leave History"}
                path={isAdminOrHR ? "Leave Management / History" : "Leave / History"}
            />

            {/* Statistics Cards */}
            <div className='grid xl:grid-cols-5 lg:grid-cols-3 sm:grid-cols-2 grid-cols-1 gap-15 mb-20'>
                <div className='bg-bg-dark rounded-lg shadow-light p-20'>
                    <p className='text-text-secondary text-sm mb-2'>Total Requests</p>
                    <p className='text-3xl font-bold'>{stats.totalRequests}</p>
                </div>
                <div className='bg-bg-dark rounded-lg shadow-light p-20'>
                    <p className='text-text-secondary text-sm mb-2'>Approved</p>
                    <p className='text-3xl font-bold text-success'>{stats.approved}</p>
                </div>
                <div className='bg-bg-dark rounded-lg shadow-light p-20'>
                    <p className='text-text-secondary text-sm mb-2'>Pending</p>
                    <p className='text-3xl font-bold text-warning'>{stats.pending}</p>
                </div>
                <div className='bg-bg-dark rounded-lg shadow-light p-20'>
                    <p className='text-text-secondary text-sm mb-2'>Rejected</p>
                    <p className='text-3xl font-bold text-danger'>{stats.rejected}</p>
                </div>
                <div className='bg-bg-dark rounded-lg shadow-light p-20'>
                    <p className='text-text-secondary text-sm mb-2'>Total Days</p>
                    <p className='text-3xl font-bold text-blue'>{stats.totalDays}</p>
                    <p className='text-xs text-text-secondary mt-1'>Approved</p>
                </div>
            </div>

            {/* Leave History Table */}
            <div className='bg-bg-dark rounded-lg shadow-light md:p-20 p-15'>
                <div className='flex items-center justify-between gap-15 mb-20'>
                    <div>
                        <p className='text-[16px]/[19.2px] font-medium'>
                            {isAdminOrHR ? 'All Leave Requests History' : 'Your Leave History'}
                        </p>
                        <p className='text-xs text-text-secondary mt-1'>
                            {isAdminOrHR
                                ? 'Complete history of all employee leave requests'
                                : 'Track your past leave requests and their status'
                            }
                        </p>
                    </div>
                    <button className='btn btn-primary'>
                        Export to Excel
                    </button>
                </div>
                <div className='react-data-table'>
                    <ReactDataTable
                        columns={isAdminOrHR ? adminColumns : employeeColumns}
                        data={filteredItems}
                        pagination
                        paginationResetDefaultPage={resetPaginationToggle}
                        subHeader
                        subHeaderComponent={subHeaderComponentMemo}
                        highlightOnHover
                        defaultSortFieldId={1}
                        defaultSortAsc={false}
                    />
                </div>
            </div>

            {/* View Details Modal */}
            {viewDetailsModal && selectedLeave && (
                <>
                    <div className='fixed p-15 w-full max-w-[700px] left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] z-[6]'>
                        <div className='py-10 md:px-10 px-[7px] bg-bg-dark rounded-lg shadow-light'>
                            <h5 className="text-[16px]/[19.2px] font-medium md:px-10 px-[7px] py-10 mb-20 text-center border-b border-border">
                                Leave Request Details
                            </h5>
                            <div className='my-10 md:px-10 px-[7px] max-h-[70vh] overflow-auto cus-scrollbar'>
                                <div className='grid grid-cols-12 gap-20'>
                                    {/* Employee Info */}
                                    {isAdminOrHR && (
                                        <div className='col-span-12 pb-15 border-b border-border'>
                                            <div className='flex items-center gap-15'>
                                                <img
                                                    src={selectedLeave._rawData.avatar}
                                                    alt={selectedLeave._rawData.employeeName}
                                                    className='w-[60px] h-[60px] rounded-full object-cover'
                                                />
                                                <div>
                                                    <p className='font-medium text-lg'>{selectedLeave._rawData.employeeName}</p>
                                                    <p className='text-sm text-text-secondary'>{selectedLeave._rawData.employeeId}</p>
                                                    <p className='text-sm text-text-secondary'>{selectedLeave._rawData.department}</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Leave Details */}
                                    <div className='col-span-6'>
                                        <p className='text-sm text-text-secondary mb-1'>Leave Type</p>
                                        <p className={`font-medium ${leaveTypes[selectedLeave._rawData.leaveType].color}`}>
                                            {leaveTypes[selectedLeave._rawData.leaveType].label}
                                        </p>
                                    </div>
                                    <div className='col-span-6'>
                                        <p className='text-sm text-text-secondary mb-1'>Status</p>
                                        {getStatusBadge(selectedLeave._rawData.status)}
                                    </div>

                                    <div className='col-span-6'>
                                        <p className='text-sm text-text-secondary mb-1'>From Date</p>
                                        <p className='font-medium'>{formatDate(selectedLeave._rawData.fromDate)}</p>
                                    </div>
                                    <div className='col-span-6'>
                                        <p className='text-sm text-text-secondary mb-1'>To Date</p>
                                        <p className='font-medium'>{formatDate(selectedLeave._rawData.toDate)}</p>
                                    </div>

                                    <div className='col-span-6'>
                                        <p className='text-sm text-text-secondary mb-1'>Total Days</p>
                                        <p className='font-medium text-blue text-lg'>{selectedLeave._rawData.days} days</p>
                                    </div>
                                    <div className='col-span-6'>
                                        <p className='text-sm text-text-secondary mb-1'>Applied Date</p>
                                        <p className='font-medium'>{formatDate(selectedLeave._rawData.appliedDate)}</p>
                                    </div>

                                    <div className='col-span-12'>
                                        <p className='text-sm text-text-secondary mb-1'>Reason</p>
                                        <p className='font-medium'>{selectedLeave._rawData.reason}</p>
                                    </div>

                                    {/* Approval Info */}
                                    {selectedLeave._rawData.status !== 'pending' && (
                                        <>
                                            <div className='col-span-12 pt-15 border-t border-border'>
                                                <p className='text-sm font-medium mb-10'>Approval Details</p>
                                            </div>
                                            <div className='col-span-6'>
                                                <p className='text-sm text-text-secondary mb-1'>Approved/Rejected By</p>
                                                <p className='font-medium'>{selectedLeave._rawData.approver}</p>
                                            </div>
                                            <div className='col-span-6'>
                                                <p className='text-sm text-text-secondary mb-1'>Date</p>
                                                <p className='font-medium'>{formatDate(selectedLeave._rawData.approvalDate)}</p>
                                            </div>
                                            <div className='col-span-12'>
                                                <p className='text-sm text-text-secondary mb-1'>Comments</p>
                                                <p className='font-medium'>{selectedLeave._rawData.comments}</p>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className='md:px-10 px-[7px] flex gap-10 justify-end py-10 pt-20 border-t border-border'>
                                <button
                                    onClick={() => setViewDetailsModal(false)}
                                    className='btn btn-primary'
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                    <div
                        onClick={() => setViewDetailsModal(false)}
                        className='fixed z-[5] w-full h-full left-0 top-0 bg-bg-transparent backdrop-blur-[5px]'
                    ></div>
                </>
            )}
        </>
    );
}
